import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { PcOnlyHintModal } from '@/components/PcOnlyHintModal';
import { mobileAdmin } from '@/lib/mobileAdminPaths';
import { cn } from '@/lib/cn';
import {
  EntryReviewAlert,
  entryDraftErrmsgForDisplay,
  type EntryReviewReason,
} from '@/lib/entryReviewUi';

const searchFieldClass =
  'w-full rounded-2xl border border-slate-200/90 bg-white px-4 py-3.5 text-base text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-teal-500/60 focus:outline-none focus:ring-2 focus:ring-teal-500/25';
const searchLabelClass = 'mb-2 block text-sm font-semibold text-slate-700';

type Row = {
  id: number;
  entry_source?: string;
  serial_number: string | null;
  title_text: string | null;
  body_text: string | null;
  valid_end_date: string;
  draft_status: string;
  created_at: string;
};

type ListResp = {
  ok: boolean;
  items: Row[];
  total: number;
  page: number;
  per_page: number;
  wechat_draft_ready?: boolean;
};

type EntryDetail = {
  id: number;
  site_id?: number;
  entry_source?: string;
  title_text: string | null;
  body_text: string | null;
  contact_phone: string | null;
  valid_end_date: string;
  draft_status: string;
  draft_errmsg: string | null;
  review_reason?: EntryReviewReason;
  matched_sensitive_words?: string[];
  created_at: string;
};

type GetEntryResp = { ok: boolean; entry?: EntryDetail; err?: string; wechat_draft_ready?: boolean };

const PER_PAGE = 12;

const DRAFT_HINT =
  '请先在电脑端完成微信公众号接入。';

function draftShort(status: string, entrySource?: string): { text: string; ok: boolean } {
  if (entrySource === 'mp_user') {
    if (status === 'awaiting_review') return { text: '小程序待审', ok: false };
    if (status === 'review_rejected') return { text: '已拒绝', ok: false };
    return { text: '小程序已展示', ok: true };
  }
  if (status === 'success') return { text: '已进草稿箱', ok: true };
  if (status === 'awaiting_review') return { text: '待审未进箱', ok: false };
  if (status === 'review_rejected') return { text: '已拒绝', ok: false };
  return { text: '未进草稿箱', ok: false };
}

function sourceLabel(src: string | undefined): string {
  if (src === 'manual') return '后台';
  if (src === 'mp_user') return '小程序';
  return '金数据';
}

function isMpUserEntry(src: string | undefined): boolean {
  return src === 'mp_user';
}

export function MobileEntriesPage() {
  const { currentSiteId } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewParam = searchParams.get('review');
  const [items, setItems] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [wechatDraftReady, setWechatDraftReady] = useState(false);
  const prevSiteIdRef = useRef(0);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<EntryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [pcOpen, setPcOpen] = useState(false);

  const load = useCallback(async (opts?: { page?: number }) => {
    const p = opts?.page ?? page;
    setLoading(true);
    setErr(null);
    try {
      const r = await apiFetch<ListResp>('list_entries', {
        method: 'GET',
        query: {
          page: p,
          per_page: PER_PAGE,
          ...(appliedQ.trim() !== '' ? { q: appliedQ.trim() } : {}),
        },
      });
      if (r.ok) {
        setPage(p);
        setItems(r.items);
        setTotal(r.total);
        setWechatDraftReady(Boolean(r.wechat_draft_ready));
      } else {
        setErr('加载失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
        setErr(null);
        return;
      }
      setErr(e instanceof Error ? e.message : '错误');
    } finally {
      setLoading(false);
    }
  }, [page, appliedQ]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (currentSiteId < 1) return;
    const prev = prevSiteIdRef.current;
    prevSiteIdRef.current = currentSiteId;
    if (prev !== 0 && prev !== currentSiteId) {
      setPage(1);
      setDetailOpen(false);
      setDetailId(null);
      setDetail(null);
      void load({ page: 1 });
    }
  }, [currentSiteId, load]);

  useEffect(() => {
    const flash = (location.state as { flash?: string } | null)?.flash;
    if (!flash) return;
    setToast(flash);
    navigate('.', { replace: true, state: {} });
  }, [location.state, navigate]);

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await apiFetch<GetEntryResp>('get_entry', { method: 'GET', query: { id } });
      if (r.ok && r.entry) {
        setDetail(r.entry);
        if (typeof r.wechat_draft_ready === 'boolean') {
          setWechatDraftReady(r.wechat_draft_ready);
        }
      }
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  function openDetail(id: number) {
    setDetailId(id);
    setDetailOpen(true);
    void loadDetail(id);
  }

  useEffect(() => {
    if (!reviewParam) return;
    const id = parseInt(reviewParam, 10);
    if (!Number.isFinite(id) || id < 1) return;
    setDetailId(id);
    setDetailOpen(true);
    void loadDetail(id);
    const next = new URLSearchParams(searchParams);
    next.delete('review');
    setSearchParams(next, { replace: true });
  }, [reviewParam, loadDetail, searchParams, setSearchParams]);

  function closeDetail() {
    setDetailOpen(false);
    setDetailId(null);
    setDetail(null);
  }

  async function deleteEntry() {
    if (!detailId) return;
    if (!window.confirm(`确定删除 #${detailId}？不可恢复。`)) return;
    setActionBusy(true);
    setToast(null);
    try {
      const r = await apiFetch<{ ok: boolean }>('delete_entry', {
        method: 'POST',
        body: JSON.stringify({ entry_id: detailId }),
      });
      if (r.ok) {
        setToast('已删除');
        closeDetail();
        await load();
      } else {
        setToast('删除失败');
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '失败');
    } finally {
      setActionBusy(false);
    }
  }

  async function pushDraft() {
    if (!detailId) return;
    setActionBusy(true);
    setToast(null);
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        draft_status?: string;
        matched_sensitive_words?: string[];
      }>('push_entry_draft', { method: 'POST', body: JSON.stringify({ entry_id: detailId }) });
      if (r.ok) {
        setToast(r.draft_status === 'success' ? '已提交草稿' : r.detail || '已处理');
        await load();
        void loadDetail(detailId);
      } else if (r.err === 'sensitive_blocked') {
        setToast(r.detail || '仍含敏感词，请先修改并保存');
        setDetail((prev) =>
          prev
            ? {
                ...prev,
                review_reason: 'sensitive',
                matched_sensitive_words: r.matched_sensitive_words ?? prev.matched_sensitive_words,
              }
            : prev,
        );
      } else {
        setToast(r.detail || r.err || '失败');
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '失败');
    } finally {
      setActionBusy(false);
    }
  }

  async function rejectEntryReview() {
    if (!detailId) return;
    if (!window.confirm('确定拒绝本条？将标记为「审核已拒绝」，不会写入公众号草稿箱。')) {
      return;
    }
    setActionBusy(true);
    setToast(null);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('reject_entry_review', {
        method: 'POST',
        body: JSON.stringify({ entry_id: detailId }),
      });
      if (r.ok) {
        setToast('已拒绝本条');
        await load();
        void loadDetail(detailId);
      } else {
        setToast(r.detail || r.err || '操作失败');
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '操作失败');
    } finally {
      setActionBusy(false);
    }
  }

  async function approveMpUserEntry() {
    if (!detailId || !detail) return;
    if (!window.confirm('确定通过审核？通过后将在小程序公开展示。')) return;
    setActionBusy(true);
    setToast(null);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('approve_mp_user_entry', {
        method: 'POST',
        body: JSON.stringify({ entry_id: detailId, site_id: detail.site_id }),
      });
      if (r.ok) {
        setToast('已通过审核');
        await load();
        void loadDetail(detailId);
      } else {
        setToast(r.detail || r.err || '审核失败');
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : '审核失败');
    } finally {
      setActionBusy(false);
    }
  }

  function runSearch() {
    setAppliedQ(qInput.trim());
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  /** 列表按 id DESC；第 1 页首条即当前条件下最新一条（全量或搜索结果） */
  const latestVisibleId = useMemo(
    () => (page === 1 && items.length > 0 ? items[0]!.id : null),
    [page, items]
  );

  if (currentSiteId < 1) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-slate-900">信息查询</h1>
        <p className="mt-2 text-base text-slate-600">请先选择站点。</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">信息查询</h1>
        <p className="mt-2 text-base text-slate-600">
          点按卡片查看详情；待审核条目标「审核」或打开详情后可「通过 / 拒绝」。完整编辑请用电脑端。
        </p>
      </div>

      <Link
        to={mobileAdmin.register}
        className="flex min-h-14 w-full items-center justify-center rounded-2xl bg-gradient-to-b from-teal-500 to-teal-600 text-lg font-semibold text-white shadow-md shadow-teal-900/20 active:scale-[0.99]"
      >
        ＋ 新建登记
      </Link>

      <Card title="搜索">
        <div className="flex flex-col gap-3">
          <label className="block">
            <span className={searchLabelClass}>关键词</span>
            <input
              className={searchFieldClass}
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              placeholder="标题 / 正文 / 手机 / 编号"
            />
          </label>
          <Button type="button" variant="primary" className="min-h-12 w-full text-base" onClick={runSearch}>
            搜索
          </Button>
        </div>
      </Card>

      {toast ? (
        <p className="rounded-2xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-base text-slate-800">{toast}</p>
      ) : null}

      {loading ? (
        <Spinner label="加载列表" />
      ) : err ? (
        <EmptyState>{err}</EmptyState>
      ) : items.length === 0 ? (
        <EmptyState>暂无数据</EmptyState>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((r) => {
            const dw = draftShort(r.draft_status, r.entry_source);
            const isLatest = latestVisibleId !== null && r.id === latestVisibleId;
            return (
              <div
                key={r.id}
                className={cn(
                  'touch-manipulation overflow-hidden rounded-3xl border text-left shadow-sm',
                  isLatest
                    ? 'border-teal-400/70 bg-gradient-to-br from-teal-50/95 via-white to-teal-50/40 shadow-md shadow-teal-900/10 ring-2 ring-teal-400/50'
                    : 'border-slate-200/80 bg-white'
                )}
              >
                <button
                  type="button"
                  onClick={() => openDetail(r.id)}
                  className="w-full p-5 text-left active:bg-slate-50/80"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm text-slate-400">#{r.id}</span>
                      {isLatest ? (
                        <span className="rounded-full bg-teal-600 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm">
                          最新
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={
                        r.entry_source === 'manual'
                          ? 'rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-900'
                          : 'text-[10px] text-slate-500'
                      }
                    >
                      {sourceLabel(r.entry_source)}
                    </span>
                  </div>
                  <p className="mt-2 line-clamp-3 text-lg font-semibold leading-snug text-slate-900">
                    {r.title_text || r.body_text || '（无标题）'}
                  </p>
                  <p className="mt-2 text-sm text-slate-600">有效期至 {r.valid_end_date}</p>
                  <p className={cn('mt-1 text-sm font-medium', dw.ok ? 'text-emerald-700' : 'text-amber-800')}>
                    草稿：{dw.text}
                  </p>
                </button>
                {r.draft_status === 'awaiting_review' ? (
                  <div className="border-t border-amber-200/90 bg-amber-50/90 px-4 pb-4 pt-3">
                    <Button
                      type="button"
                      variant="primary"
                      className="min-h-12 w-full text-base font-semibold"
                      disabled={actionBusy}
                      onClick={() => openDetail(r.id)}
                    >
                      审核
                    </Button>
                  </div>
                ) : null}
              </div>
            );
          })}
          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            summary={total > 0 ? `共 ${total} 条` : undefined}
          />
        </div>
      )}

      <Modal
        open={detailOpen}
        onClose={closeDetail}
        title={detailId ? `信息详情 #${detailId}` : '信息详情'}
        wide
        footer={
          !detailLoading && detail ? (
            <div className="space-y-3">
              {detail.draft_status === 'awaiting_review' ? (
                <>
                  <p className="text-xs leading-relaxed text-slate-600">
                    {isMpUserEntry(detail.entry_source) || detail.review_reason === 'mp_user'
                      ? '本条为小程序用户投稿。核对后点「通过并展示」，或点「拒绝」。'
                      : '核对后点「通过」写入草稿箱，或点「拒绝」标记为已拒绝。'}
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      className="min-h-12 w-full text-base sm:w-auto"
                      disabled={actionBusy}
                      onClick={() => setPcOpen(true)}
                    >
                      完整编辑（电脑端）
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      className="min-h-12 w-full text-base sm:w-auto"
                      disabled={actionBusy}
                      onClick={() => void rejectEntryReview()}
                    >
                      {actionBusy ? '处理中…' : '拒绝'}
                    </Button>
                    {isMpUserEntry(detail.entry_source) || detail.review_reason === 'mp_user' ? (
                      <Button
                        type="button"
                        variant="primary"
                        className="min-h-12 w-full text-base sm:w-auto"
                        disabled={actionBusy}
                        onClick={() => void approveMpUserEntry()}
                      >
                        {actionBusy ? '处理中…' : '通过并展示'}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="primary"
                        className="min-h-12 w-full text-base sm:w-auto"
                        disabled={!wechatDraftReady || actionBusy}
                        title={!wechatDraftReady ? DRAFT_HINT : undefined}
                        onClick={() => void pushDraft()}
                      >
                        {actionBusy ? '处理中…' : '通过'}
                      </Button>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="danger"
                    className="min-h-12 w-full text-base"
                    disabled={actionBusy}
                    onClick={() => void deleteEntry()}
                  >
                    删除
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      className="min-h-12 w-full text-base sm:w-auto"
                      disabled={!wechatDraftReady || actionBusy || detail.draft_status === 'success'}
                      title={
                        !wechatDraftReady
                          ? DRAFT_HINT
                          : detail.draft_status === 'success'
                            ? '本条已成功写入公众号草稿'
                            : undefined
                      }
                      onClick={() => void pushDraft()}
                    >
                      {actionBusy ? '处理中…' : detail.draft_status === 'success' ? '已在草稿箱' : '上传公众号草稿箱'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      className="min-h-12 w-full text-base sm:w-auto"
                      disabled={actionBusy}
                      onClick={() => setPcOpen(true)}
                    >
                      完整编辑（电脑端）
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      className="min-h-12 w-full text-base sm:w-auto"
                      disabled={actionBusy}
                      onClick={() => void deleteEntry()}
                    >
                      删除
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : null
        }
      >
        {detailLoading ? (
          <Spinner label="加载" />
        ) : detail ? (
          <div className="space-y-5">
            <EntryReviewAlert
              draftStatus={detail.draft_status}
              reviewReason={detail.review_reason}
              matchedWords={detail.matched_sensitive_words}
            />
            <div className="space-y-4 text-base">
              <p>
                <span className="text-sm font-medium text-slate-500">标题</span>
                <span className="mt-1 block font-semibold text-slate-900">{detail.title_text || '—'}</span>
              </p>
              <p>
                <span className="text-sm font-medium text-slate-500">正文</span>
                <span className="mt-1 block whitespace-pre-wrap leading-relaxed text-slate-800">{detail.body_text || '—'}</span>
              </p>
              <p>
                <span className="text-sm font-medium text-slate-500">电话</span>
                <span className="mt-1 block text-slate-800">{detail.contact_phone || '—'}</span>
              </p>
              <p>
                <span className="text-sm font-medium text-slate-500">有效期至</span>
                <span className="mt-1 block text-slate-800">{detail.valid_end_date}</span>
              </p>
              <p>
                <span className="text-sm font-medium text-slate-500">草稿</span>
                <span className="mt-1 block text-slate-800">{draftShort(detail.draft_status, detail.entry_source).text}</span>
              </p>
              {(() => {
                const errHint = entryDraftErrmsgForDisplay(detail);
                return errHint ? <p className="text-sm text-rose-700">{errHint}</p> : null;
              })()}
            </div>
          </div>
        ) : (
          <p className="text-base text-slate-500">无法加载</p>
        )}
      </Modal>

      <PcOnlyHintModal
        open={pcOpen}
        onClose={() => setPcOpen(false)}
        feature="信息登记的完整编辑、重新下载图片等"
      />
    </div>
  );
}
